import {
  Fornecedor,
  Loja,
  Produto,
  Tributacao,
} from '~/pages/EntradaNFE/protocols';
import { calculaICMS } from '../Calculos/Icms';
import { toast } from 'react-toastify';

const DEBUG_PREFIX = '[EntradaNFE][buscaInfoTributacaoEntrada]';

const snapshotTributacao = (tributacao?: Tributacao) =>
  tributacao
    ? {
        cod_tributacao: tributacao.cod_tributacao,
        des_tributacao: tributacao.des_tributacao,
        tipo_tributacao: tributacao.tipo_tributacao,
        cod_situacao_tributaria: tributacao.cod_situacao_tributaria,
        val_icms: tributacao.val_icms,
        val_reducao: tributacao.val_reducao,
        tipo_encerramento_diferido: tributacao.tipo_encerramento_diferido,
      }
    : null;

const snapshotProdutoTributacao = (produto: Produto) => ({
  cod_produto: produto.cod_produto,
  des_produto: produto.des_produto,
  cod_ncm: produto.cod_ncm,
  tipo_tributacao_produto: produto.tipo_tributacao,
  cod_tributacao_produto: produto.cod_tributacao,
  cod_trib_entrada: produto.cod_trib_entrada,
  tipo_trib_entrada: produto.tipo_trib_entrada,
  cod_sit_trib_entrada: produto.cod_sit_trib_entrada,
  des_trib_entrada: produto.des_trib_entrada,
  per_icms_entrada: produto.per_icms_entrada,
  val_icms_entrada: produto.val_icms_entrada,
  val_reducao_entrada: produto.val_reducao_entrada,
  flg_nao_pis_cofins: produto.flg_nao_pis_cofins,
  tipo_nao_pis_cofins: produto.tipo_nao_pis_cofins,
  per_aliq_pis: produto.per_aliq_pis,
  per_aliq_cofins: produto.per_aliq_cofins,
  cst_pis: produto.cst_pis,
  cst_cofins: produto.cst_cofins,
  cst_pis_cof_entrada: produto.cst_pis_cof_entrada,
});

export function buscaInfoTributacaoEntrada(
  produto: Produto,
  loja: Loja,
  fornecedor: Fornecedor,
  tributacoes: Tributacao[],
  cod_tributacao: number,
  cod_trib_simples: number,
  cfop: number,
  flg_optante_simples: boolean,
): void {
  const identificacao = `produto=${produto.cod_produto} | CFOP=${cfop}`;
  let resultadoDepuracao = 'processando';

  console.groupCollapsed(`${DEBUG_PREFIX} INÍCIO | ${identificacao}`);
  console.log(`${DEBUG_PREFIX} 01 - Parâmetros recebidos`, {
    origem: 'EntradaNFEContent.prepareItemForInsertion',
    produto: snapshotProdutoTributacao(produto),
    loja: { uf: loja.uf, des_uf: loja.des_uf },
    fornecedor: {
      des_uf: fornecedor.des_uf,
      flg_regime_st: fornecedor.flg_regime_st,
      flg_nao_transf_icms: fornecedor.flg_nao_transf_icms,
    },
    regras: {
      cod_tributacao_recebido: cod_tributacao,
      cod_trib_simples,
      cfop,
      flg_optante_simples,
    },
    tributacoes_disponiveis: tributacoes.map(snapshotTributacao),
  });

  try {
    if (
      flg_optante_simples &&
      [0, 2].includes(produto.tipo_tributacao) &&
      cod_trib_simples > 0
    ) {
      console.log(
        `${DEBUG_PREFIX} 02 - Simples Nacional alterou o código a pesquisar`,
        {
          origem_anterior: 'buscaTributacao',
          cod_tributacao_anterior: cod_tributacao,
          origem_nova: 'cod_trib_simples',
          cod_tributacao_novo: cod_trib_simples,
        },
      );
      cod_tributacao = cod_trib_simples;
    } else {
      console.log(`${DEBUG_PREFIX} 02 - Código recebido mantido`, {
        origem: 'buscaTributacao',
        cod_tributacao,
        motivo: 'regra do Simples Nacional não aplicável',
      });
    }

    const tributacao = tributacoes.find(
      (item) => item.cod_tributacao === cod_tributacao,
    );

    console.log(`${DEBUG_PREFIX} 03 - Resultado da busca pelo código`, {
      criterio: { cod_tributacao },
      encontrado: Boolean(tributacao),
      tributacao: snapshotTributacao(tributacao),
    });

    if (!tributacao) {
      resultadoDepuracao = 'interrompido: tributação não encontrada';
      console.warn(`${DEBUG_PREFIX} Tributação não encontrada`, {
        cod_tributacao_pesquisado: cod_tributacao,
        codigos_disponiveis: tributacoes.map((item) => item.cod_tributacao),
        produto: snapshotProdutoTributacao(produto),
      });
      toast.warning('Tributação não selecionada');
      return;
    }
    let guiaST = false; // Operações Interestaduais e o produto é ST

    if (
      loja.uf !== fornecedor.des_uf &&
      produto.tipo_tributacao === 3 &&
      !fornecedor.flg_regime_st
    ) {
      guiaST = true;
    }

    console.log(`${DEBUG_PREFIX} 04 - Avaliação da regra de guia ST`, {
      loja_uf: loja.uf,
      fornecedor_uf: fornecedor.des_uf,
      ufs_diferentes: loja.uf !== fornecedor.des_uf,
      tipo_tributacao_produto: produto.tipo_tributacao,
      produto_eh_st: produto.tipo_tributacao === 3,
      fornecedor_flg_regime_st: fornecedor.flg_regime_st,
      guiaST,
    });

    if ([0, 2].includes(tributacao.tipo_tributacao) && guiaST) {
      // Busca a tributação do tipo ST
      const tributacaoST = tributacoes.find((reg) => {
        return (
          reg.val_icms === produto.val_icms_entrada &&
          reg.val_reducao === produto.val_reducao_entrada &&
          reg.tipo_tributacao === 3
        );
      });

      console.log(`${DEBUG_PREFIX} 05 - Busca da tributação ST equivalente`, {
        motivo: 'tributação localizada é normal/reduzida e guiaST=true',
        criterio: {
          val_icms: produto.val_icms_entrada,
          val_reducao: produto.val_reducao_entrada,
          tipo_tributacao: 3,
        },
        encontrado: Boolean(tributacaoST),
        tributacao: snapshotTributacao(tributacaoST),
      });

      if (tributacaoST) {
        produto.cod_trib_entrada = tributacaoST.cod_tributacao;
        produto.val_icms_entrada = tributacaoST.val_icms;
        produto.tipo_trib_entrada = tributacaoST.tipo_tributacao;
        produto.val_reducao_entrada = tributacaoST.val_reducao;

        produto.per_icms_entrada = calculaICMS(
          0,
          tributacaoST.val_icms,
          tributacaoST.val_reducao,
          tributacaoST.tipo_tributacao,
          true,
          false,
        );

        produto.cod_sit_trib_entrada = tributacaoST.cod_situacao_tributaria;
        produto.des_trib_entrada = tributacaoST.des_tributacao;
        produto.tipo_encerramento_diferido =
          tributacaoST.tipo_encerramento_diferido;

        console.log(`${DEBUG_PREFIX} 06 - Tributação ST aplicada ao produto`, {
          origem: 'tributacaoST equivalente encontrada na lista',
          produto: snapshotProdutoTributacao(produto),
        });

        // const cfop = defineCFOP(0, false, )
      } else {
        resultadoDepuracao = 'erro: tributação ST equivalente não encontrada';
        console.error(`${DEBUG_PREFIX} Falha ao localizar tributação ST`, {
          criterio: {
            val_icms: produto.val_icms_entrada,
            val_reducao: produto.val_reducao_entrada,
            tipo_tributacao: 3,
          },
          produto: snapshotProdutoTributacao(produto),
        });
        throw new Error(
          'Não existe nenhum código de tributação que tenha mesmo Perc. de ICMS e Red.BC para Tipo Tributação: Substituição',
        );
      }
    } else {
      produto.cod_trib_entrada = tributacao.cod_tributacao;
      produto.val_icms_entrada = tributacao.val_icms;
      produto.tipo_trib_entrada = tributacao.tipo_tributacao;
      produto.val_reducao_entrada = tributacao.val_reducao;
      produto.per_icms_entrada = calculaICMS(
        0,
        tributacao.val_icms,
        tributacao.val_reducao,
        tributacao.tipo_tributacao,
        true,
        false,
      );
      produto.cod_sit_trib_entrada = tributacao.cod_situacao_tributaria;
      produto.des_trib_entrada = tributacao.des_tributacao;
      produto.tipo_encerramento_diferido =
        tributacao.tipo_encerramento_diferido;

      console.log(`${DEBUG_PREFIX} 05 - Tributação selecionada aplicada`, {
        origem: 'tributação localizada pelo cod_tributacao',
        tributacao: snapshotTributacao(tributacao),
        produto: snapshotProdutoTributacao(produto),
      });
    }

    console.log(
      `${DEBUG_PREFIX} 07 - Estado após tributação principal`,
      snapshotProdutoTributacao(produto),
    );

    const deveAplicarRegraNaoTransferencia =
      fornecedor.flg_nao_transf_icms &&
      [0, 2].includes(produto.tipo_trib_entrada);
    console.log(
      `${DEBUG_PREFIX} 08 - Avaliação da regra de não transferência de ICMS`,
      {
        fornecedor_flg_nao_transf_icms: fornecedor.flg_nao_transf_icms,
        tipo_trib_entrada_atual: produto.tipo_trib_entrada,
        regra_aplicavel: deveAplicarRegraNaoTransferencia,
      },
    );

    if (deveAplicarRegraNaoTransferencia) {
      const tributacaoOutros = tributacoes.find((trib) => {
        return (
          trib.val_icms === 0 &&
          trib.cod_situacao_tributaria === '090' &&
          trib.tipo_tributacao === 6
        );
      });
      console.log(`${DEBUG_PREFIX} 08.1 - Busca por Outros ICMS`, {
        criterio: {
          val_icms: 0,
          cod_situacao_tributaria: '090',
          tipo_tributacao: 6,
        },
        encontrado: Boolean(tributacaoOutros),
        tributacao: snapshotTributacao(tributacaoOutros),
      });
      if (tributacaoOutros) {
        produto.cod_trib_entrada = tributacaoOutros.cod_tributacao;
        produto.val_icms_entrada = tributacaoOutros.val_icms;
        produto.tipo_trib_entrada = tributacaoOutros.tipo_tributacao;
        produto.val_reducao_entrada = tributacaoOutros.val_reducao;
        produto.per_icms_entrada = calculaICMS(
          0,
          tributacaoOutros.val_icms,
          tributacaoOutros.val_reducao,
          tributacaoOutros.tipo_tributacao,
          true,
          false,
        );
        produto.cod_sit_trib_entrada = tributacaoOutros.cod_situacao_tributaria;
        produto.des_trib_entrada = tributacaoOutros.des_tributacao;
        produto.tipo_encerramento_diferido =
          tributacaoOutros.tipo_encerramento_diferido;
        console.log(
          `${DEBUG_PREFIX} 09 - Tributação substituída por Outros ICMS`,
          {
            origem: 'fornecedor.flg_nao_transf_icms',
            produto: snapshotProdutoTributacao(produto),
          },
        );
      } else {
        resultadoDepuracao = 'erro: tributação Outros ICMS não encontrada';
        console.error(`${DEBUG_PREFIX} Falha na regra de não transferência`, {
          produto: snapshotProdutoTributacao(produto),
        });
        throw new Error(
          `Não existe nenhuma tributação do tipo 'Outros ICMS' que tenha 0% de Valor de ICMS e CST 090.`,
        );
      }
    }

    if (
      flg_optante_simples &&
      [0, 2].includes(produto.tipo_tributacao) &&
      cod_trib_simples > 0
    ) {
      produto.cod_trib_entrada = cod_trib_simples;
      produto.per_icms_entrada =
        tributacao.cod_situacao_tributaria === '041' ||
        tributacao.cod_situacao_tributaria === '051'
          ? 0
          : tributacao.val_icms;
      produto.val_reducao_entrada = tributacao.val_reducao;
      produto.tipo_trib_entrada = tributacao.tipo_tributacao;
      produto.cod_sit_trib_entrada = tributacao.cod_situacao_tributaria;
      console.log(`${DEBUG_PREFIX} 10 - Regra do Simples Nacional aplicada`, {
        origem: 'cod_trib_simples + dados da tributação selecionada',
        cod_trib_simples,
        tributacao: snapshotTributacao(tributacao),
        produto: snapshotProdutoTributacao(produto),
      });
    } else if (
      String(cfop).slice(1, 4) === '556'
      // [0, 2].includes(produto.tipo_tributacao)
    ) {
      const tributacaoOutros = tributacoes.find((trib) => {
        return (
          trib.val_icms === 0 &&
          trib.cod_situacao_tributaria === '090' &&
          trib.tipo_tributacao === 6
        );
      });
      console.log(`${DEBUG_PREFIX} 10 - CFOP de uso e consumo: busca CST 090`, {
        cfop,
        sufixo_cfop: String(cfop).slice(1, 4),
        encontrado: Boolean(tributacaoOutros),
        tributacao: snapshotTributacao(tributacaoOutros),
      });
      if (tributacaoOutros) {
        // ICMS - CST 090 sem ICMS
        produto.cod_trib_entrada = tributacaoOutros.cod_tributacao;
        produto.per_icms_entrada = tributacaoOutros.val_icms;
        produto.val_icms_entrada = tributacaoOutros.val_icms;
        produto.val_reducao_entrada = tributacaoOutros.val_reducao;
        produto.tipo_trib_entrada = tributacaoOutros.tipo_tributacao;
        produto.cod_sit_trib_entrada = tributacaoOutros.cod_situacao_tributaria;

        // Para produtos de uso e consumo (CST 090), zerar valores de ICMS
        produto.val_tot_bc_icms = 0;
        produto.val_tot_bc_icms_ger = 0;
        produto.val_tot_icms_op = 0;
        produto.val_tot_icms_op_gerenc = 0;
        produto.val_tot_isento = 0;
        produto.val_tot_outros_icms = 0;

        // Calcular val_outros conforme regra específica para CFOP 1556
        if (produto.qtd_embalagem > 0) {
          produto.val_tot_outros_icms =
            (produto.qtd_total / produto.qtd_embalagem) * produto.val_tabela;
        }

        // PIS/COFINS - Configuração específica para CFOP 1556 (notas de uso e consumo)
        produto.flg_nao_pis_cofins = true;
        produto.tipo_nao_pis_cofins = -1;
        produto.per_aliq_pis = 1.65;
        produto.per_aliq_cofins = 4.6;
        produto.cst_pis = '50';
        produto.cst_cofins = '50';
        produto.cst_pis_cof_entrada = '1';
        console.log(
          `${DEBUG_PREFIX} 11 - Regras de uso e consumo aplicadas ao produto`,
          {
            origem: 'CFOP terminado em 556',
            produto: snapshotProdutoTributacao(produto),
            totais_icms: {
              val_tot_bc_icms: produto.val_tot_bc_icms,
              val_tot_bc_icms_ger: produto.val_tot_bc_icms_ger,
              val_tot_icms_op: produto.val_tot_icms_op,
              val_tot_icms_op_gerenc: produto.val_tot_icms_op_gerenc,
              val_tot_isento: produto.val_tot_isento,
              val_tot_outros_icms: produto.val_tot_outros_icms,
            },
          },
        );
      } else {
        resultadoDepuracao = 'erro: CST 090 para uso e consumo não encontrada';
        console.error(`${DEBUG_PREFIX} Falha na regra de uso e consumo`, {
          cfop,
          produto: snapshotProdutoTributacao(produto),
        });
        throw new Error(
          `Produto '${produto.des_produto} - ${produto.cod_produto}' de Uso e Consumo mas não foi encontrada tributação do tipo 'Outros ICMS' e CST 090`,
        );
      }
    } else if (String(cfop).slice(1, 4) === '910') {
      // Produtos de bonificação (CFOP terminando em 910)
      // Configurar para não ter PIS/COFINS
      produto.flg_nao_pis_cofins = true;
      produto.tipo_nao_pis_cofins = -1;
      produto.cst_pis = '98';
      produto.cst_cofins = '98';
      produto.val_pis = 0;
      produto.val_cofins = 0;
      produto.val_base_pis = 0;
      produto.val_base_cofins = 0;
      produto.val_isento_pis_cofins = 0;
      produto.val_aliq_zero = 0;
      produto.val_monofasico = 0;
      produto.val_substituicao = 0;
      produto.val_nao_tribut = 0;
      produto.val_imune = 0;
      produto.val_suspensao = 0;
      console.log(`${DEBUG_PREFIX} 10 - Regra de bonificação aplicada`, {
        origem: 'CFOP terminado em 910',
        produto: snapshotProdutoTributacao(produto),
      });
    } else {
      console.log(
        `${DEBUG_PREFIX} 10 - Nenhuma regra especial de CFOP/Simples`,
        {
          cfop,
          sufixo_cfop: String(cfop).slice(1, 4),
        },
      );
    }

    // Verificar se é CST 041 ou 051 - zerar percentuais de ICMS
    if (
      produto.cod_sit_trib_entrada === '041' ||
      produto.cod_sit_trib_entrada === '051'
    ) {
      // Para CST 041 e 051, zerar percentuais de ICMS
      produto.per_icms_entrada = 0;
      produto.val_icms_entrada = 0;
      produto.val_reducao_entrada = 0;
      console.log(`${DEBUG_PREFIX} 12 - Percentuais de ICMS zerados`, {
        origem: `CST ${produto.cod_sit_trib_entrada}`,
        produto: snapshotProdutoTributacao(produto),
      });
    }

    resultadoDepuracao = 'concluído';

    // return produto;
  } catch (error) {
    resultadoDepuracao = resultadoDepuracao.startsWith('erro')
      ? resultadoDepuracao
      : 'erro inesperado';
    console.error(`${DEBUG_PREFIX} ERRO | ${identificacao}`, {
      error,
      produto_no_momento_do_erro: snapshotProdutoTributacao(produto),
    });
    throw error;
  } finally {
    console.log(`${DEBUG_PREFIX} FIM | ${identificacao}`, {
      resultado: resultadoDepuracao,
      produto_final: snapshotProdutoTributacao(produto),
    });
    console.groupEnd();
  }
}
